import React from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import LayoutDefault from '../Landing/layouts/LayoutDefault';
import { BLOG_POSTS, BLOG_META } from '../../data/blogPosts';
import {
    getBlogCategoryBySlug,
    getBlogCategoryEntries,
    slugifyBlogCategory
} from '../../data/blogCategories';

const BlogList = () => {
    const { categorySlug } = useParams();
    const sortedPosts = Object.entries(BLOG_POSTS)
        .map(([slug, post]) => ({
            ...post,
            slug
        }))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const categoryEntries = getBlogCategoryEntries();
    const activeCategory = categorySlug ? getBlogCategoryBySlug(categorySlug) : null;
    const isCategoryPage = Boolean(categorySlug);
    const categoryPosts = activeCategory?.posts || [];
    const featuredPost = sortedPosts[0];
    const sidePosts = sortedPosts.slice(1, 3);
    const remainingPosts = sortedPosts.slice(3);
    const gridPosts = isCategoryPage ? categoryPosts : remainingPosts;
    const formatDate = date => new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
    const getCategoryPath = category => `/blog/category/${slugifyBlogCategory(category)}`;

    return (
        <LayoutDefault>
            <BlogSurface>
                <BlogContainer>
                    {isCategoryPage ? (
                        <CategoryHero>
                            <CategoryBreadcrumbs>
                                <BackCategoryLink to="/blog">Blog</BackCategoryLink>
                                <span aria-hidden="true">/</span>
                                <span>{activeCategory?.name || 'Category'}</span>
                            </CategoryBreadcrumbs>
                            <Kicker>{BLOG_META.siteName}</Kicker>
                            <h1>{activeCategory?.name || 'Category not found'}</h1>
                            <Subtitle>
                                {activeCategory?.description || 'We could not find that blog category.'}
                            </Subtitle>
                            {activeCategory && (
                                <CategoryCount>
                                    {categoryPosts.length} {categoryPosts.length === 1 ? 'post' : 'posts'} in this category
                                </CategoryCount>
                            )}
                        </CategoryHero>
                    ) : (
                        <BlogHeader>
                            <Kicker>{BLOG_META.siteName}</Kicker>
                            <h1>Luckynote Blog</h1>
                            <Subtitle>{BLOG_META.description}</Subtitle>
                        </BlogHeader>
                    )}

                    {!isCategoryPage && featuredPost && (
                        <LeadGrid aria-label="Featured blog posts">
                            <HeroPost>
                                <ImageLink to={`/blog/${featuredPost.slug}`} aria-label={featuredPost.title}>
                                    <FeaturedImage src={featuredPost.featuredImage} alt={featuredPost.title} />
                                </ImageLink>
                                <PostMetaLine>
                                    <span>{formatDate(featuredPost.createdAt)}</span>
                                    {featuredPost.category && (
                                        <CategoryMetaLink
                                            to={{
                                                pathname: getCategoryPath(featuredPost.category),
                                                state: { preserveScroll: true }
                                            }}
                                        >
                                            {featuredPost.category}
                                        </CategoryMetaLink>
                                    )}
                                </PostMetaLine>
                                <FeaturedTitle>
                                    <Link to={`/blog/${featuredPost.slug}`}>{featuredPost.title}</Link>
                                </FeaturedTitle>
                                <FeaturedExcerpt>{featuredPost.excerpt}</FeaturedExcerpt>
                            </HeroPost>

                            {sidePosts.length > 0 && (
                                <SideStack>
                                    {sidePosts.map(post => (
                                        <SidePost key={post.slug}>
                                            <ImageLink to={`/blog/${post.slug}`} aria-label={post.title}>
                                                <SideImage src={post.featuredImage} alt={post.title} />
                                            </ImageLink>
                                            <PostMetaLine>
                                                <span>{formatDate(post.createdAt)}</span>
                                                {post.category && (
                                                    <CategoryMetaLink
                                                        to={{
                                                            pathname: getCategoryPath(post.category),
                                                            state: { preserveScroll: true }
                                                        }}
                                                    >
                                                        {post.category}
                                                    </CategoryMetaLink>
                                                )}
                                            </PostMetaLine>
                                            <SideTitle>
                                                <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                                            </SideTitle>
                                        </SidePost>
                                    ))}
                                </SideStack>
                            )}
                        </LeadGrid>
                    )}

                    {categoryEntries.length > 0 && (
                        <ExploreSection>
                            <h2>{isCategoryPage ? 'Discover more from the blog' : 'Explore the Luckynote blog'}</h2>
                            <CategoryList aria-label="Blog categories">
                                {categoryEntries.map(category => (
                                    <CategoryPill
                                        key={category.slug}
                                        to={{
                                            pathname: `/blog/category/${category.slug}`,
                                            state: { preserveScroll: true }
                                        }}
                                        $active={category.slug === categorySlug}
                                    >
                                        {category.name}
                                    </CategoryPill>
                                ))}
                            </CategoryList>
                        </ExploreSection>
                    )}

                    <PostsSection>
                        <SectionHeading>{isCategoryPage ? 'Posts in this category' : 'More posts'}</SectionHeading>
                        <BlogGrid>
                            {gridPosts.map(post => (
                                <BlogCard key={post.slug}>
                                    {post.featuredImage && (
                                        <ImageLink to={`/blog/${post.slug}`} aria-label={post.title}>
                                            <BlogImage src={post.featuredImage} alt={post.title} />
                                        </ImageLink>
                                    )}
                                    <BlogContent>
                                        <PostMetaLine>
                                            <span>{formatDate(post.createdAt)}</span>
                                            {post.category && (
                                                <CategoryMetaLink
                                                    to={{
                                                        pathname: getCategoryPath(post.category),
                                                        state: { preserveScroll: true }
                                                    }}
                                                >
                                                    {post.category}
                                                </CategoryMetaLink>
                                            )}
                                        </PostMetaLine>
                                        <BlogTitle>
                                            <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                                        </BlogTitle>
                                        <BlogExcerpt>{post.excerpt}</BlogExcerpt>
                                        <ReadMore to={`/blog/${post.slug}`}>
                                            Read article <span aria-hidden="true">-&gt;</span>
                                        </ReadMore>
                                    </BlogContent>
                                </BlogCard>
                            ))}
                        </BlogGrid>
                    </PostsSection>
                </BlogContainer>
            </BlogSurface>
        </LayoutDefault>
    );
};

const BlogSurface = styled.div`
    background:
        radial-gradient(circle at 12% 8%, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0) 24rem),
        linear-gradient(180deg, #fbf7f1 0%, #f7f1e9 52%, #fbf8f3 100%);
    color: #14110f;
    margin-top: -1px;
    min-height: 100dvh;
`;

const BlogContainer = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: 72px 24px 96px;

    @media (max-width: 768px) {
        padding: 42px 18px 72px;
    }
`;

const BlogHeader = styled.div`
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
`;

const CategoryHero = styled.header`
    margin: 0 auto 58px;
    max-width: 760px;
    text-align: center;

    h1 {
        color: #14110f;
        font-family: Georgia, 'Times New Roman', serif;
        font-size: 56px;
        font-weight: 700;
        letter-spacing: 0;
        line-height: 1.04;
        margin: 0 0 20px;
        text-wrap: balance;
    }

    @media (max-width: 640px) {
        h1 {
            font-size: 38px;
        }
    }

`;

const CategoryBreadcrumbs = styled.nav`
    align-items: center;
    color: rgba(20, 17, 15, 0.52);
    display: flex;
    flex-wrap: wrap;
    font-size: 0.92rem;
    gap: 10px;
    justify-content: center;
    margin-bottom: 28px;
`;

const BackCategoryLink = styled(Link)`
    color: rgba(20, 17, 15, 0.68) !important;
    font-weight: 650;
    text-decoration: none !important;

    &:hover,
    &:focus-visible {
        color: #14110f !important;
    }

    &:focus-visible {
        outline: 2px solid rgba(96, 67, 39, 0.38);
        outline-offset: 4px;
    }
`;

const CategoryCount = styled.p`
    color: rgba(20, 17, 15, 0.5);
    font-size: 0.95rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    margin: 28px 0 0;
    text-transform: uppercase;
`;

const Kicker = styled.p`
    color: rgba(20, 17, 15, 0.58);
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    margin: 0 0 18px;
    text-transform: uppercase;
`;

const Subtitle = styled.p`
    color: rgba(20, 17, 15, 0.66);
    font-size: clamp(1.05rem, 2vw, 1.25rem);
    line-height: 1.6;
    margin: 0 auto;
    max-width: 560px;
    text-wrap: pretty;
`;

const LeadGrid = styled.section`
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(280px, 0.94fr);
    gap: clamp(28px, 4vw, 52px);
    margin-bottom: 96px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
        margin-bottom: 76px;
    }
`;

const HeroPost = styled.article`
    min-width: 0;
`;

const SideStack = styled.div`
    display: grid;
    gap: 34px;
`;

const ImageLink = styled(Link)`
    background: rgba(20, 17, 15, 0.05);
    border: 1px solid rgba(20, 17, 15, 0.12);
    border-radius: 10px;
    display: block;
    overflow: hidden;
    position: relative;
    text-decoration: none;
    transition: box-shadow 260ms ease, transform 260ms ease, border-color 260ms ease;

    &:hover,
    &:focus-visible {
        border-color: rgba(20, 17, 15, 0.22);
        box-shadow: 0 18px 42px rgba(59, 46, 35, 0.16);
        outline: none;
        transform: translateY(-3px);
    }
`;

const FeaturedImage = styled.img`
    width: 100%;
    aspect-ratio: 5 / 3;
    display: block;
    object-fit: cover;
    transition: transform 380ms ease;

    ${ImageLink}:hover &,
    ${ImageLink}:focus-visible & {
        transform: scale(1.025);
    }
`;

const SidePost = styled.article`
    min-width: 0;
`;

const SideImage = styled(FeaturedImage)`
    aspect-ratio: 5 / 3;
`;

const FeaturedTitle = styled.h2`
    font-family: Georgia, 'Times New Roman', serif;
    font-size: clamp(2.25rem, 3.45vw, 3.45rem);
    font-weight: 700;
    line-height: 1.02;
    letter-spacing: 0;
    margin: 12px 0 16px;
    text-wrap: balance;

    && {
        color: #12100e;
    }

    a {
        color: #12100e;
        text-decoration: none;

        &:hover {
            color: #604327;
        }
    }
`;

const FeaturedExcerpt = styled.p`
    color: rgba(20, 17, 15, 0.72);
    font-size: 1.08rem;
    line-height: 1.65;
    margin: 0;
    max-width: 670px;
    text-wrap: pretty;
`;

const SideTitle = styled.h3`
    font-size: 1.08rem;
    font-weight: 700;
    line-height: 1.28;
    margin: 8px 0 0;

    && {
        color: #14110f;
    }

    a {
        color: #14110f;
        text-decoration: none;

        &:hover {
            color: #604327;
        }
    }
`;

const PostMetaLine = styled.div`
    align-items: center;
    color: rgba(20, 17, 15, 0.48);
    display: flex;
    flex-wrap: wrap;
    font-size: 0.9rem;
    gap: 8px 12px;
    margin-top: 22px;

    span + span {
        color: rgba(20, 17, 15, 0.64);
    }
`;

const CategoryMetaLink = styled(Link)`
    color: rgba(20, 17, 15, 0.64) !important;
    font-weight: 650;
    text-decoration: none !important;

    &:hover,
    &:focus-visible {
        color: #604327 !important;
    }

    &:focus-visible {
        outline: 2px solid rgba(96, 67, 39, 0.35);
        outline-offset: 3px;
    }
`;

const ExploreSection = styled.section`
    margin: 0 auto 64px;
    text-align: center;

    h2 {
        color: #14110f;
        font-family: Georgia, 'Times New Roman', serif;
        font-size: 28px;
        font-weight: 600;
        line-height: 1.12;
        margin: 0 0 24px;
        text-wrap: balance;
    }

    && h2 {
        color: #14110f;
        font-weight: 600;
    }

    @media (max-width: 640px) {
        h2 {
            font-size: 24px;
        }
    }
`;

const CategoryList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    justify-content: center;
`;

const CategoryPill = styled(Link).attrs(props => ({
    style: {
        color: props.$active ? '#fcf9f7' : '#14110f'
    }
}))`
    background: ${props => props.$active ? 'rgba(20, 17, 15, 0.9)' : 'rgba(255, 255, 255, 0.42)'};
    border: 1px solid ${props => props.$active ? 'rgba(20, 17, 15, 0.9)' : 'rgba(20, 17, 15, 0.18)'};
    border-radius: 10px;
    box-shadow: 0 6px 18px rgba(59, 46, 35, 0.05);
    font-size: 0.98rem;
    font-weight: 600;
    line-height: 1;
    padding: 14px 18px;
    text-decoration: none !important;
    transition: background 180ms ease, border-color 180ms ease, color 180ms ease, transform 180ms ease;

    &:hover,
    &:focus-visible {
        background: ${props => props.$active ? 'rgba(20, 17, 15, 0.9)' : 'rgba(255, 255, 255, 0.76)'};
        border-color: ${props => props.$active ? 'rgba(20, 17, 15, 0.9)' : 'rgba(20, 17, 15, 0.32)'};
        transform: translateY(-1px);
    }

    &:focus-visible {
        outline: 2px solid rgba(96, 67, 39, 0.4);
        outline-offset: 4px;
    }
`;

const PostsSection = styled.section`
    margin-top: 0;
`;

const SectionHeading = styled.h2`
    color: #14110f;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 1.75rem;
    line-height: 1.1;
    margin: 0 0 26px;

    && {
        color: #14110f;
        font-weight: 700;
    }
`;

const BlogGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 42px 32px;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 620px) {
        grid-template-columns: 1fr;
    }
`;

const BlogCard = styled.article`
    min-width: 0;
`;

const BlogImage = styled(FeaturedImage)`
    aspect-ratio: 5 / 3;
`;

const BlogContent = styled.div`
    padding: 0;
`;

const BlogTitle = styled.h2`
    font-size: 1.18rem;
    font-weight: 700;
    line-height: 1.25;
    margin: 8px 0 10px;
    text-wrap: balance;

    && {
        color: #14110f;
    }

    a {
        color: #14110f;
        text-decoration: none;

        &:hover {
            color: #604327;
        }
    }
`;

const BlogExcerpt = styled.p`
    color: rgba(20, 17, 15, 0.68);
    font-size: 0.96rem;
    line-height: 1.58;
    margin: 0 0 16px;
    text-wrap: pretty;
`;

const ReadMore = styled(Link)`
    color: #604327;
    display: inline-flex;
    font-weight: 700;
    gap: 6px;
    line-height: 1.2;
    text-decoration: none;
    transition: color 180ms ease, transform 180ms ease;

    && {
        color: #604327;
    }

    &:hover {
        color: #14110f;
        transform: translateX(2px);
    }

    &:focus-visible {
        outline: 2px solid rgba(96, 67, 39, 0.45);
        outline-offset: 4px;
    }

    &&:hover {
        color: #14110f;
    }
`;

export default BlogList;
